import type {
  CleanlistTriggerConfig,
  CustomGraph,
  GraphIssue,
  RunPeriod,
  TriggerConfig,
  TriggerSchedule,
} from '@/types/custom-playbooks';

/**
 * An agent's play finds its leads per schedule period ("500 new leads per
 * week", agents plan W2): the trigger's `perPeriod`, delivered in batches
 * across poll ticks. When the trigger seeds companies and Find People follows,
 * the period counts the people Find People delivers ("50 new people per
 * week"). A library play keeps `maxPerCycle`, capped at one search page a run.
 * Pure, so the words and the checks are tested here.
 */

/** The most leads an agent's play finds in one period (the server's MAX_PER_PERIOD). */
export const MAX_PER_PERIOD = 5000;

/** A library play's cap on one run (the server's BATCH_SIZE, one search page). */
export const MAX_PER_CYCLE = 200;

/** What the volume reads before anyone sets it. */
export const DEFAULT_VOLUME = 100;

/** How the quota line names the schedule's period: "per week", "every 3 days", "per quarter". */
export function periodPhrase(schedule: TriggerSchedule | undefined): string {
  switch (schedule?.frequency) {
    case 'daily':
      return 'per day';
    case 'weekdays':
      return 'per weekday';
    case 'weekly':
      return 'per week';
    case 'every_n_days':
      return `every ${schedule.everyNDays ?? 3} days`;
    case 'monthly':
      return 'per month';
    case 'quarterly':
      return 'per quarter';
    default:
      // Every 30 minutes has no period; the trigger warns about it.
      return 'per period';
  }
}

/**
 * What an agent's play counts per period: the people Find People delivers
 * when the trigger seeds companies and a Find People step follows, the
 * companies a company search finds without one, the leads a people trigger
 * finds.
 */
export type VolumeUnit = 'people' | 'companies' | 'leads';

/**
 * Whether a trigger seeds companies, as the server reads it
 * (`trigger_seed_kind`): a Cleanlist company search or a CRM Account. A CRM
 * Deal carries its Account as a company, matching `triggerOutputKind`.
 */
export function seedsCompanies(
  config: Partial<TriggerConfig> | undefined
): boolean {
  if (
    config?.source === 'crustdata_watch' ||
    config?.source === 'company_signal'
  )
    return true;
  if (config?.source === 'google_sheet')
    return config.sheet?.recordKind === 'company';
  // A Google Doc version is a company kind record (`trigger_seed_kind`).
  if (config?.source === 'google_doc') return true;
  if (config?.source === 'crm')
    return ['Account', 'Deal'].includes(config.crm?.objectType ?? '');
  if (config?.source === 'cleanlist')
    return (
      config.cleanlist?.kind === 'company' || config.cleanlist?.kind === 'jobs'
    );
  return false;
}

/** Whether a Find People step sits anywhere below the graph's trigger. */
function findsPeopleBelowTrigger(graph: CustomGraph): boolean {
  const trigger = graph.nodes.find((node) => node.type === 'trigger');
  if (!trigger) return false;
  const types = new Map(graph.nodes.map((node) => [node.id, node.type]));
  const seen = new Set<string>();
  const stack = [trigger.id];
  while (stack.length) {
    const id = stack.pop()!;
    for (const edge of graph.edges) {
      if (edge.source !== id || seen.has(edge.target)) continue;
      if (types.get(edge.target) === 'find_people') return true;
      seen.add(edge.target);
      stack.push(edge.target);
    }
  }
  return false;
}

function isScheduled(schedule: TriggerSchedule | undefined): boolean {
  return (
    Boolean(schedule) &&
    typeof schedule === 'object' &&
    Boolean(schedule.frequency) &&
    schedule.frequency !== 'continuous'
  );
}

/**
 * Whether the trigger runs in schedule periods, as the server reads it
 * (`_in_periods`): a Cleanlist trigger with a `perPeriod` of 1 or more on a
 * daily, weekly, every N days, monthly or quarterly schedule. Outside a
 * period the volume
 * (`perPeriod` or `maxPerCycle`) is what one run takes.
 */
export function inPeriods(config: Partial<TriggerConfig> | undefined): boolean {
  if (
    config?.source !== 'cleanlist' &&
    config?.source !== 'crustdata_watch' &&
    config?.source !== 'google_sheet'
  )
    return false;
  // Every Sheet row each period: every row up to the read cap by default.
  if (config.source === 'google_sheet' && config.sheet?.scope === 'all_rows')
    return isScheduled(config.schedule);
  const perPeriod =
    config.source === 'google_sheet'
      ? config.sheet?.perPeriod
      : config.source === 'crustdata_watch'
        ? config.watch?.perPeriod
        : config.cleanlist?.perPeriod;
  return (
    typeof perPeriod === 'number' &&
    Number.isInteger(perPeriod) &&
    perPeriod >= 1 &&
    isScheduled(config.schedule)
  );
}

/**
 * The unit the trigger's volume counts with this trigger config, in this
 * graph. People only in a period of a play that counts people: outside one
 * the server takes that many companies a run. The config is the trigger's
 * live one, schedule included; the graph tells whether Find People follows
 * it, and no graph reads as no Find People.
 */
export function volumeUnit(
  config: Partial<TriggerConfig> | undefined,
  graph: CustomGraph | null | undefined
): VolumeUnit {
  if (!seedsCompanies(config)) return 'leads';
  return inPeriods(config) && graph && findsPeopleBelowTrigger(graph)
    ? 'people'
    : 'companies';
}

/**
 * The server's `counts_people`: the graph's trigger seeds companies and a
 * Find People step follows it, behind a Branch or not. The agent's records
 * per run then count the people Find People delivers, and so does the
 * trigger's perPeriod once a period opens (`volumeUnit`).
 */
export function countsPeople(graph: CustomGraph | null | undefined): boolean {
  const trigger = graph?.nodes.find((node) => node.type === 'trigger');
  return (
    Boolean(graph && trigger) &&
    seedsCompanies(trigger?.config as Partial<TriggerConfig>) &&
    findsPeopleBelowTrigger(graph as CustomGraph)
  );
}

const UNIT_LABEL: Readonly<Record<VolumeUnit, string>> = {
  people: 'People',
  companies: 'Companies',
  leads: 'Leads',
};

/** The field's label: "People per week", "Leads every 3 days". */
export function periodLabel(
  schedule: TriggerSchedule | undefined,
  unit: VolumeUnit = 'leads'
): string {
  return UNIT_LABEL[unit] + ' ' + periodPhrase(schedule);
}

/**
 * What a schedule period counted, from its run's `trigger_meta.period`: people
 * when Find People's deliveries count, companies for a company search, leads
 * for a people trigger.
 */
export function periodNoun(
  period: Pick<RunPeriod, 'unit' | 'kind'> | null | undefined
): VolumeUnit {
  if (period?.unit === 'people') return 'people';
  return period?.kind === 'company' ? 'companies' : 'leads';
}

/**
 * The quota an agent's play shows: its `perPeriod`, else the `maxPerCycle`
 * it was saved with (a scheduled play fires once a period, so that is what
 * it took each period), else the default.
 */
export function perPeriodValue(
  cleanlist: Pick<CleanlistTriggerConfig, 'perPeriod' | 'maxPerCycle'>
): number {
  const value = cleanlist.perPeriod ?? cleanlist.maxPerCycle ?? DEFAULT_VOLUME;
  return Math.max(1, Math.min(MAX_PER_PERIOD, value));
}

/**
 * The server's warnings about `perPeriod`, in plain words. They name no unit:
 * the field's label says people, companies or leads.
 */
export const PER_PERIOD_WORDS: Readonly<Record<string, string>> = {
  trigger_per_period: 'Pick between 1 and 5,000.',
  trigger_crm_per_period: 'Up to 250,000.',
  trigger_per_period_schedule:
    'Choose daily, weekly, every few days, monthly or quarterly under How often.',
};

/** An issue with the server's `perPeriod` warnings put in plain words. */
export function issueInWords(issue: GraphIssue): GraphIssue {
  const words = PER_PERIOD_WORDS[issue.code];
  return words ? { ...issue, message: words } : issue;
}

/**
 * The client mirror of the server's `perPeriod` checks on a trigger
 * (`trigger_per_period`, `trigger_per_period_schedule`): warnings, so a draft
 * saves and the play can't be turned on until they're fixed.
 */
export function perPeriodIssues(
  config: Partial<TriggerConfig> | undefined,
  nodeId: string
): GraphIssue[] {
  if (
    !config ||
    (config.source !== 'cleanlist' &&
      config.source !== 'crustdata_watch' &&
      config.source !== 'google_sheet')
  )
    return [];
  if (
    config.source === 'google_sheet' &&
    config.sheet?.scope === 'all_rows' &&
    !isScheduled(config.schedule)
  )
    return [
      {
        nodeId,
        code: 'trigger_per_period_schedule',
        message: PER_PERIOD_WORDS.trigger_per_period_schedule,
      },
    ];
  const perPeriod =
    config.source === 'google_sheet'
      ? config.sheet?.perPeriod
      : config.source === 'crustdata_watch'
        ? config.watch?.perPeriod
        : config.cleanlist?.perPeriod;
  if (perPeriod === undefined || perPeriod === null) return [];
  if (
    typeof perPeriod !== 'number' ||
    !Number.isInteger(perPeriod) ||
    perPeriod < 1 ||
    perPeriod > MAX_PER_PERIOD
  ) {
    return [
      {
        nodeId,
        code: 'trigger_per_period',
        message: PER_PERIOD_WORDS.trigger_per_period,
      },
    ];
  }
  if (!isScheduled(config.schedule)) {
    return [
      {
        nodeId,
        code: 'trigger_per_period_schedule',
        message: PER_PERIOD_WORDS.trigger_per_period_schedule,
      },
    ];
  }
  return [];
}
